import { getMapData } from "@mappedin/mappedin-js";
import { StepByStep } from "@mappedin/step-by-step";
import axios from "axios";

 

// Fetch MappedIn configuration for a user
const fetchMappedInConfig = async (userId) => {
  if (!userId) {
    console.log("[MappedIn Service] No userId provided, using default config");
    return;
  }

  try {
    console.log("[MappedIn Service] Fetching config for userId:", userId);
    const response = await axios.get(
      `/api/wayfinder/mappedin-settings/${userId}`,
    );

    if (response.data.success && response.data.data) {
      const data = response.data.data;

      // If enabled and has valid credentials, use them
      if (data.enabled && data.apiKey && data.apiSecret && data.mapId) {
        console.log("[MappedIn Service] Using custom config from settings");
        return {
          key: data.apiKey,
          secret: data.apiSecret,
          mapId: data.mapId,
          usePointrMap: data.usePointrMap || false,
          startLocation: data.startLocation || null,
          mapLink: data.mapLink || '',
          mappedInStartLocation: data.mappedInStartLocation || '',
          mappedInStartLocationName: data.mappedInStartLocationName || '',
        };
      }
    }

    console.log("[MappedIn Service] Using default config");
    // return;
  } catch (error) {
    console.error("[MappedIn Service] Error fetching config:", error);
    console.log("[MappedIn Service] Falling back to default config");
    // return;
  }
};

/**
 * BULK DIRECTIONS - Load map ONCE, calculate directions for ALL locations, then release.
 * This prevents memory issues by avoiding repeated getMapData() calls.
 * Use this instead of looping getMappedInDirections() for bulk operations.
 * 
 * @param {string} userId - User ID for fetching config
 * @param {string} startLocationId - External ID of start location
 * @param {string} startLocationName - Name of start location (fallback)
 * @param {Array} locationsToProcess - Array of { externalId, name, _id } objects
 * @returns {Array} Results array with { success, text, distance, error } for each location
 */
export const bulkGetDirectionsFromStart = async (
  userId,
  startLocationId,
  startLocationName,
  locationsToProcess
) => {
  try {
    console.log(`[Bulk Directions] Processing ${locationsToProcess.length} locations`);
    
    // Fetch configuration based on userId
    const MAPPEDIN_CONFIG = await fetchMappedInConfig(userId);
    
    // ONE getMapData call for all locations
    const mapData = await getMapData(MAPPEDIN_CONFIG);
    console.log("[Bulk Directions] Map data loaded once for all locations");
    
    const enterpriseLocations = mapData.getByType("enterprise-location");
    console.log(`[Bulk Directions] Found ${enterpriseLocations.length} enterprise locations`);
    
    // Find start location
    const startLoc = enterpriseLocations.find(
      (loc) => loc.externalId === startLocationId || loc.name === startLocationName
    );
    
    if (!startLoc) {
      throw new Error(`Start location not found: ${startLocationName || startLocationId}`);
    }
    
    console.log(`[Bulk Directions] Start location: ${startLoc.name} (${startLoc.externalId})`);
    
    const results = [];
    
    // Process each location
    for (let i = 0; i < locationsToProcess.length; i++) {
      const target = locationsToProcess[i];
      
      if (i % 50 === 0) {
        console.log(`[Bulk Directions] Processing ${i + 1}/${locationsToProcess.length}...`);
      }
      
      try {
        // Find end location
        const endLoc = enterpriseLocations.find(
          (loc) => loc.externalId === target.externalId
        );
        
        if (!endLoc) {
          results.push({
            ...target,
            success: false,
            error: 'Location not found in map'
          });
          continue;
        }
        
        // Get directions
        const directions = await mapData.getDirections(startLoc, endLoc);
        
        if (!directions) {
          results.push({
            ...target,
            success: false,
            error: 'No directions returned'
          });
          continue;
        }
        
        // Generate step-by-step instructions
        const stepByStep = new StepByStep(mapData, directions);
        await stepByStep.generate();
        const instructions = stepByStep.instructions;
        
        // Convert to text
        const destinationName = endLoc.name || target.name || 'your destination';
        const rawText = convertDirectionsToText(instructions, destinationName);
        const text = formatTextWithSSMLBreaks(rawText);
        
        results.push({
          ...target,
          success: true,
          text,
          originalText: rawText,
          instructions,
          distance: directions.distance ? Math.round(directions.distance) : null,
          estimatedTime: directions.duration || 0,
        });
        
      } catch (err) {
        console.error(`[Bulk Directions] Error for ${target.name}:`, err.message);
        results.push({
          ...target,
          success: false,
          error: err.message
        });
      }
    }
    
    console.log(`[Bulk Directions] Completed: ${results.filter(r => r.success).length} successful, ${results.filter(r => !r.success).length} failed`);
    
    // mapData goes out of scope here — GC can reclaim it
    return results;
    
  } catch (error) {
    console.error("[Bulk Directions] Fatal error:", error);
    throw error;
  }
};

// Functional approach for getting MappedIn directions using enterprise locations
export const getMappedInDirections = async (
  externalId,
  userId = null,
  spaceName = null,
  startLocationId, // Default start location name
  startLocationName
) => {
  try {
    console.log(
      "[MappedIn Frontend] Getting directions for externalId:",
      externalId,
    );
    console.log("[MappedIn Frontend] Fallback spaceName:", spaceName);
    console.log("[MappedIn Frontend] User ID:", userId);

    // Fetch configuration based on userId
    const MAPPEDIN_CONFIG = await fetchMappedInConfig(userId);

    console.log('Mappedin Config', MAPPEDIN_CONFIG);

    console.log("[MappedIn Frontend] Using config:", {
      key: MAPPEDIN_CONFIG.key.substring(0, 10) + "...",
      mapId: MAPPEDIN_CONFIG.mapId,
    });

    // Get map data — this is all we need for directions, no WebGL required
    const mapData = await getMapData(MAPPEDIN_CONFIG);
    console.log("[MappedIn Frontend] Map data loaded");

    try {
      // Get enterprise locations
      const enterpriseLocations = mapData.getByType("enterprise-location");
      console.log(
        `[MappedIn Frontend] Found ${enterpriseLocations.length} enterprise locations`,
      );

      // Log all available enterprise locations for debugging
      console.log("[MappedIn Frontend] Available enterprise locations:");
      enterpriseLocations.forEach((loc, index) => {
        console.log(
          `  ${index + 1}. Name: "${loc.name}", ExternalId: "${loc.externalId}"`,
        );
      });

      // Find start location (Interactive Display / Kiosk)
      const startLocation = enterpriseLocations.find(
        (loc) =>
          loc.externalId === startLocationId || loc.name === startLocationName,
      );

      if (!startLocation) {
        console.error(
          "[MappedIn Frontend] Could not find start location (LOC-1IP30ZO0 or Holographic Display)",
        );
      } else {
        console.log(
          "[MappedIn Frontend] Start location found:",
          startLocation.name,
          startLocation.externalId,
        );
      }

      // Find target location by externalId
      let endLocation = null;
      if (externalId && externalId.trim()) {
        const searchExternalId = externalId.trim();
        console.log(
          `[MappedIn Frontend] Searching for externalId: "${searchExternalId}"`,
        );

        endLocation = enterpriseLocations.find(
          (loc) => loc.externalId && loc.externalId.trim() === searchExternalId,
        );                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    

        if (endLocation) {
          console.log(
            "[MappedIn Frontend] Found location by externalId:",
            endLocation.name,                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 
            endLocation.externalId,
          );                                                                                
        } else {                                                                                    
          console.warn(
            `[MappedIn Frontend] No location found with externalId: "${searchExternalId}"`,
          );                                                                        
        }
      }

      // Fallback to spaceName if externalId not found
      if (!endLocation && spaceName) {
        console.log(
          "[MappedIn Frontend] ExternalId not found, trying spaceName fallback",
        );
        const spaces = mapData.getByType("space");
        console.log(`[MappedIn Frontend] Found ${spaces.length} spaces`);

        endLocation = spaces.find(
          (s) =>
            s.name &&
            s.name.toLowerCase().trim() === spaceName.toLowerCase().trim(),
        );

        if (endLocation) {
          console.log(
            "[MappedIn Frontend] Found location by spaceName:",
            endLocation.name,
          );
        }
      }

      // Additional fallback: try to find by name matching externalId
      if (!endLocation && externalId) {
        console.log(
          "[MappedIn Frontend] Trying to find by name matching externalId:",
          externalId,
        );
        endLocation = enterpriseLocations.find(
          (loc) =>
            loc.name &&
            loc.name.toLowerCase().includes(externalId.toLowerCase()),
        );                                

        if (endLocation) {
          console.log(
            "[MappedIn Frontend] Found location by name match:",
            endLocation.name,                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
            endLocation.externalId,
          );
        }
      }

      if (!startLocation || !endLocation) {
        const errorMsg = !startLocation
          ? "Could not find start location"
          : `Could not find end location ${spaceName})`;

        console.error("[MappedIn Frontend] Error:", errorMsg);
        console.log(
          "[MappedIn Frontend] Available externalIds:",
          enterpriseLocations
            .map((loc) => loc.externalId)
            .filter(Boolean)
            .join(", "),
        );

        throw new Error(errorMsg);
      }

      console.log(
        "[MappedIn Frontend] Found locations:",
        `${startLocation.name} (${startLocation.externalId || "N/A"})`,
        "->",
        `${endLocation.name} (${endLocation.externalId || "N/A"})`,
      );

      // Get directions using mapData instead of mapView
      const directions = await mapData.getDirections(
        startLocation,
        endLocation,
      );

      if (!directions) {
        throw new Error("No directions found");
      }

      console.log("[MappedIn Frontend] Directions calculated successfully");

      // Use StepByStep package for landmark-based directions
      const stepByStep = new StepByStep(mapData, directions);
      await stepByStep.generate();
      const instructions = stepByStep.instructions;

      console.log("instructions", instructions, "stepByStep", stepByStep);

      console.log(
        "[MappedIn Frontend] Step-by-step instructions generated:",
        instructions.length,
        "steps",
      );

      // Use the location name for text generation
      const destinationName =
        endLocation.name || spaceName || "your destination";

      // Convert to readable text with enhanced formatting
      const directionsText = convertDirectionsToText(
        instructions,
        destinationName,
      );

      // Format with SSML break tags
      const formattedText = formatTextWithSSMLBreaks(directionsText);

      console.log("[MappedIn Frontend] Original text:", directionsText);
      console.log(
        "[MappedIn Frontend] Formatted text with SSML:",
        formattedText,
      );

      return {
        success: true,
        text: formattedText,
        originalText: directionsText,
        instructions: instructions,
        totalDistance: directions.distance || 0,
        estimatedTime: directions.duration || 0,
        spaceName: destinationName,
        externalId: endLocation.externalId || null,
      };
    } catch (error) {
      throw error;
    }
  } catch (error) {
    console.error("[MappedIn Frontend] Error getting directions:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Convert MappedIn directions to readable text using StepByStep instructions
export const convertDirectionsToText = (instructions, destinationName) => {
  // Build text from step-by-step instructions - just the raw directions
  let text = '';

  // Process each instruction using the text property from StepByStep
  if (instructions && instructions.length > 0) {
    instructions.forEach((instruction) => {
      // Use the instruction.text property provided by StepByStep
      if (instruction.text) {
        text += `${instruction.text}. `;
      }
    });
  }

  return text.trim();
};

// Format text with SSML break tags for video narration (REMOVED - now handled by backend concatenation)
// This function is kept for backward compatibility but should not add extra content
export const formatTextWithSSMLBreaks = (text) => {
  // Just return the text as-is, no additional formatting
  // The backend concatenation will handle all SSML breaks and additional text
  return text;
};

// Convert text directions to VTT caption format
export const convertTextToVTT = (text, videoDuration = 30) => {
  // Split by sentences instead of lines for better caption timing
  const sentences = text.match(/[^\.!?]+[\.!?]+/g) || [text];
  const timePerSentence = videoDuration / sentences.length;

  let vtt = "WEBVTT\n\n";

  sentences.forEach((sentence, index) => {
    if (sentence.trim()) {
      const startTime = formatVTTTime(index * timePerSentence);
      const endTime = formatVTTTime((index + 1) * timePerSentence);

      vtt += `${index + 1}\n`;
      vtt += `${startTime} --> ${endTime}\n`;
      vtt += `${sentence.trim()}\n\n`;
    }
  });

  return vtt;
};

// Helper function to remove SSML tags for display purposes
export const removeSSMLTags = (text) => {
  if (!text) return "";
  return text.replace(/<break[^>]*>/g, "").replace(/\s+/g, " ").trim();
};

// Format time for VTT format (HH:MM:SS.mmm)
export const formatVTTTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

/**
 * Enhance MappedIn directions with AI and save to location
 * @param {string} userId - User ID
 * @param {string} locationId - Location ID
 * @param {string} rawDirections - Raw directions from MappedIn
 * @param {string} destinationName - Name of destination
 * @param {string} spaceName - MappedIn space name
 * @returns {Promise<Object>} Enhanced directions result
 */
export const enhanceDirectionsWithAI = async (userId, locationId, rawDirections, destinationName, spaceName) => {
  try {
    const response = await fetch('/api/wayfinder/step-by-step-directions/enhance-directions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        locationId,
        rawDirections,
        destinationName,
        spaceName,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to enhance directions');
    }

    return data;
  } catch (error) {
    console.error('[enhanceDirectionsWithAI] Error:', error);
    throw error;
  }
};

/**
 * Get concatenated text for a location (all fields combined)
 * @param {string} userId - User ID
 * @param {string} locationId - Location ID
 * @param {string} destinationName - Name of destination
 * @param {string} categoryId - Optional category ID
 * @param {string} subCategoryId - Optional subcategory ID
 * @returns {Promise<Object>} Concatenated text result
 */
export const getConcatenatedDirectionsText = async (userId, locationId, destinationName, categoryId = null, subCategoryId = null) => {
  try {
    const params = new URLSearchParams({
      userId,
      locationId,
      destinationName: destinationName || '',
    });

    if (categoryId) params.append('categoryId', categoryId);
    if (subCategoryId) params.append('subCategoryId', subCategoryId);

    const response = await fetch(`/api/wayfinder/step-by-step-directions/concatenated-text?${params.toString()}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get concatenated text');
    }

    return data;
  } catch (error) {
    console.error('[getConcatenatedDirectionsText] Error:', error);
    throw error;
  }
};
